import { ensureUiStyles } from './styles';
import { TOOLS, RULES, CHALLENGES, DEFAULT_TOOL_ORDER, type ChallengeTemplate } from '../core/catalog';
import type { RuleId, GameMode } from '../core/constants';
import type { Discovery } from '../core/storage';

export interface HudCallbacks {
  onRun: () => void;
  onPause: () => void;
  onReset: () => void;
  onUndo: () => void;
  onMode: (mode: GameMode) => void;
  onSelectTool: (toolId: string | null) => void;
  onRule: (rule: RuleId) => void;
  onChallenge: (c: ChallengeTemplate) => void;
  onDaily: () => void;
  onDeleteSelected: () => void;
  onRotateSelected: () => void;
  onToggleMute: () => void;
  onShare: () => void;
  onImportCode: (code: string) => void;
  onSimulateAd: () => void;
  onCloseResult: () => void;
  onPlayReplay: () => void;
}

export interface HudState {
  mode: GameMode;
  rule: RuleId;
  selectedTool: string | null;
  unlockedTools: string[];
  running: boolean;
  paused: boolean;
  muted: boolean;
  goalText: string;
  statusText: string;
  canUndo: boolean;
  discoveries: Discovery[];
  challengeProgress: Record<string, { completed: boolean; bestScore: number }>;
  debug: boolean;
  showGhostHint: boolean;
}

export class Hud {
  root: HTMLDivElement;
  private cbs: HudCallbacks;
  private state: HudState;
  private toastEl: HTMLDivElement;
  private panelEl: HTMLDivElement;
  private debugEl: HTMLDivElement;
  private adEl: HTMLDivElement;
  private ghostEl: HTMLDivElement;
  private statusEl: HTMLDivElement;
  private toolsEl: HTMLDivElement;
  private rulesEl: HTMLDivElement;
  private goalEl: HTMLDivElement;
  private runBtn: HTMLButtonElement;
  private pauseBtn: HTMLButtonElement;

  constructor(parent: HTMLElement, initial: HudState, cbs: HudCallbacks) {
    ensureUiStyles();
    this.cbs = cbs;
    this.state = { ...initial };
    this.root = document.createElement('div');
    this.root.className = 'odd-hud';
    this.root.innerHTML = `
      <div class="odd-top">
        <div class="odd-brand">
          <strong>ODD LAB</strong>
          <span data-sub>Launch Room 01</span>
        </div>
        <div class="odd-goal"><span class="dot"></span><span data-goal></span></div>
        <div class="odd-actions">
          <button class="odd-btn ghost" data-act="mute" title="Sound">🔊</button>
          <button class="odd-btn ghost" data-act="menu" title="More experiments">☰</button>
          <button class="odd-btn secondary compact" data-act="undo" title="Undo">↶</button>
          <button class="odd-btn secondary compact" data-act="reset">↻</button>
          <button class="odd-btn secondary compact" data-act="pause">Ⅱ</button>
          <button class="odd-btn primary-run" data-act="run">LAUNCH!</button>
        </div>
      </div>
      <div class="odd-modes">
        <button class="odd-chip active" data-mode="quick">Quick</button>
        <button class="odd-chip" data-mode="free">Free Lab</button>
        <button class="odd-chip" data-mode="challenge">Challenges</button>
        <button class="odd-chip" data-mode="daily">Daily</button>
      </div>
      <div class="odd-status" data-status></div>
      <div class="odd-ghost-hint" data-ghost>Drag a toy into BUDDY'S path</div>
      <div class="odd-toast" data-toast>
        <h3 data-t-title></h3>
        <p data-t-body></p>
        <div class="delta" data-t-delta></div>
        <div class="nextq" data-t-next></div>
        <div class="row">
          <button class="odd-btn secondary" data-t-close>Keep Tinkering</button>
          <button class="odd-btn" data-t-share>Copy Result</button>
          <button class="odd-btn ghost" data-t-replay>Replay</button>
        </div>
      </div>
      <div class="odd-panel" data-panel>
        <div class="odd-card" data-card></div>
      </div>
      <div class="odd-ad" data-ad>
        <div class="box">
          <h3>Simulated Ad (debug)</h3>
          <p>No real ads in v0.1 — verifying offer → start → complete flow.</p>
          <div class="prog"><div class="bar" data-ad-bar></div></div>
          <button class="odd-btn" data-ad-skip disabled>Skip in 3…</button>
        </div>
      </div>
      <div class="odd-debug" data-debug></div>
      <div class="odd-tray">
        <div class="odd-tray-label">CHOOSE A TOY</div>
        <div class="odd-rules" data-rules></div>
        <div class="odd-tray-row">
          <div class="odd-tools" data-tools></div>
          <div style="display:flex;flex-direction:column;gap:6px;">
            <button class="odd-btn secondary" data-act="rotate" title="Rotate">⟳</button>
            <button class="odd-btn danger" data-act="delete" title="Delete">✕</button>
          </div>
        </div>
      </div>
    `;
    parent.appendChild(this.root);

    this.toastEl = this.root.querySelector('[data-toast]')!;
    this.panelEl = this.root.querySelector('[data-panel]')!;
    this.debugEl = this.root.querySelector('[data-debug]')!;
    this.adEl = this.root.querySelector('[data-ad]')!;
    this.ghostEl = this.root.querySelector('[data-ghost]')!;
    this.statusEl = this.root.querySelector('[data-status]')!;
    this.toolsEl = this.root.querySelector('[data-tools]')!;
    this.rulesEl = this.root.querySelector('[data-rules]')!;
    this.goalEl = this.root.querySelector('[data-goal]')!;
    this.runBtn = this.root.querySelector('[data-act="run"]')!;
    this.pauseBtn = this.root.querySelector('[data-act="pause"]')!;

    this.bind();
    this.render();
  }

  private bind() {
    this.root.querySelector('[data-act="run"]')!.addEventListener('click', () => this.cbs.onRun());
    this.root.querySelector('[data-act="pause"]')!.addEventListener('click', () => this.cbs.onPause());
    this.root.querySelector('[data-act="reset"]')!.addEventListener('click', () => this.cbs.onReset());
    this.root.querySelector('[data-act="undo"]')!.addEventListener('click', () => this.cbs.onUndo());
    this.root.querySelector('[data-act="delete"]')!.addEventListener('click', () => this.cbs.onDeleteSelected());
    this.root.querySelector('[data-act="rotate"]')!.addEventListener('click', () => this.cbs.onRotateSelected());
    this.root.querySelector('[data-act="mute"]')!.addEventListener('click', () => this.cbs.onToggleMute());
    this.root.querySelector('[data-act="menu"]')!.addEventListener('click', () => this.openMenu());
    this.root.querySelectorAll('[data-mode]').forEach((el) => {
      el.addEventListener('click', () => {
        const mode = (el as HTMLElement).dataset.mode as GameMode;
        if (mode === 'challenge') this.openChallenges();
        else if (mode === 'daily') this.cbs.onDaily();
        else this.cbs.onMode(mode);
      });
    });
    this.toastEl.querySelector('[data-t-close]')!.addEventListener('click', () => {
      this.hideToast();
      this.cbs.onCloseResult();
    });
    this.toastEl.querySelector('[data-t-share]')!.addEventListener('click', () => this.cbs.onShare());
    this.toastEl.querySelector('[data-t-replay]')!.addEventListener('click', () => this.cbs.onPlayReplay());
    this.panelEl.addEventListener('click', (e) => {
      if (e.target === this.panelEl) this.closePanel();
    });
  }

  update(partial: Partial<HudState>) {
    this.state = { ...this.state, ...partial };
    this.render();
  }

  private render() {
    const s = this.state;
    this.goalEl.textContent = s.goalText;
    this.statusEl.textContent = s.statusText;
    (this.root.querySelector('[data-sub]') as HTMLElement).textContent =
      s.mode === 'quick'
        ? 'Quick Experiment'
        : s.mode === 'free'
          ? 'Free Lab'
          : s.mode === 'daily'
            ? 'Daily Experiment'
            : 'Challenge';
    this.root.querySelectorAll('[data-mode]').forEach((el) => {
      el.classList.toggle('active', (el as HTMLElement).dataset.mode === s.mode);
    });
    this.runBtn.textContent = s.running ? 'FLYING…' : 'LAUNCH!';
    this.runBtn.disabled = s.running && !s.paused;
    this.pauseBtn.textContent = s.paused ? '▶' : 'Ⅱ';
    this.pauseBtn.disabled = !s.running && !s.paused;
    (this.root.querySelector('[data-act="undo"]') as HTMLButtonElement).disabled = !s.canUndo;
    (this.root.querySelector('[data-act="mute"]') as HTMLButtonElement).textContent = s.muted ? '🔇' : '🔊';
    this.ghostEl.classList.toggle('show', s.showGhostHint);
    this.debugEl.classList.toggle('open', s.debug);
    this.renderTools();
    this.renderRules();
  }

  private renderTools() {
    const s = this.state;
    this.toolsEl.innerHTML = '';
    const visibleTools = s.mode === 'quick' ? DEFAULT_TOOL_ORDER.slice(0, 3) : DEFAULT_TOOL_ORDER;
    const toyIcons: Record<string, string> = {
      spring: '↟', fan: '✺', ramp: '◢', balloon: '●', magnet: '∩', crate: '▣',
      bounce_pad: '↥', rocket: '▲', bumper: '◆', portal_gate: '◎',
    };
    for (const id of visibleTools) {
      const def = TOOLS[id];
      if (!def) continue;
      const unlocked = s.unlockedTools.includes(id) || !!def.unlockDefault;
      // In free mode show all; locked ones greyed if not unlocked except free unlocks all common
      const freeAll = s.mode === 'free' || s.mode === 'challenge' || s.mode === 'daily';
      const isUnlocked = unlocked || (freeAll && ['balloon', 'magnet', 'crate', 'bounce_pad', 'rocket', 'bumper', 'portal_gate'].includes(id) && (s.mode !== 'quick'));
      // Quick mode respects unlocks strictly; free lab unlocks full set
      const showUnlocked =
        s.mode === 'quick' ? unlocked || !!def.unlockDefault : true;
      const btn = document.createElement('button');
      btn.className = `odd-tool${s.selectedTool === id ? ' selected' : ''}${!showUnlocked ? ' locked' : ''}`;
      btn.type = 'button';
      btn.dataset.tool = id;
      btn.style.setProperty('--toy-color', `#${def.color.toString(16).padStart(6, '0')}`);
      btn.innerHTML = `<span class="toy-icon">${toyIcons[id] || '◆'}</span><span>${def.name}</span><small>${def.hint}</small>`;
      btn.title = def.hint;
      if (showUnlocked) {
        btn.addEventListener('click', () => this.cbs.onSelectTool(id));
        // drag from tray via pointer
        btn.addEventListener('pointerdown', (ev) => {
          ev.preventDefault();
          this.cbs.onSelectTool(id);
          // custom event for scene to start ghost drag
          window.dispatchEvent(
            new CustomEvent('oddlab-tray-drag', {
              detail: { toolId: id, clientX: ev.clientX, clientY: ev.clientY, pointerId: ev.pointerId },
            }),
          );
        });
      }
      this.toolsEl.appendChild(btn);
    }
  }

  private renderRules() {
    this.rulesEl.innerHTML = '';
    (Object.keys(RULES) as RuleId[]).forEach((id) => {
      const r = RULES[id];
      const b = document.createElement('button');
      b.className = `odd-rule${this.state.rule === id ? ' active' : ''}`;
      b.textContent = r.name;
      b.title = r.short;
      // quick mode may lock some rules until unlock
      if (this.state.mode === 'quick' && id === 'low_gravity' && !this.state.unlockedTools.includes('low_gravity_rule')) {
        // still allow if unlocked via quick.unlockedLowGravity passed as fake tool id
      }
      b.addEventListener('click', () => this.cbs.onRule(id));
      this.rulesEl.appendChild(b);
    });
  }

  showResult(opts: {
    title: string;
    body: string;
    delta?: string;
    nextQuestion?: string;
  }) {
    (this.toastEl.querySelector('[data-t-title]') as HTMLElement).textContent = opts.title;
    (this.toastEl.querySelector('[data-t-body]') as HTMLElement).textContent = opts.body;
    (this.toastEl.querySelector('[data-t-delta]') as HTMLElement).textContent = opts.delta || '';
    (this.toastEl.querySelector('[data-t-next]') as HTMLElement).textContent = opts.nextQuestion || '';
    this.toastEl.classList.add('show');
  }

  hideToast() {
    this.toastEl.classList.remove('show');
  }

  openMenu() {
    const card = this.panelEl.querySelector('[data-card]') as HTMLDivElement;
    card.innerHTML = `
      <h2>Odd Lab Menu</h2>
      <p style="opacity:.8;font-size:13px;margin-bottom:10px;">Paper-toy physics lab. Place parts, hit Run, tweak one variable.</p>
      <div class="odd-list">
        <button class="odd-item" data-m="quick"><b>Quick Experiment</b><small>Guided first launch past the red line</small></button>
        <button class="odd-item" data-m="free"><b>Free Lab</b><small>All tools, rules, save automatically</small></button>
        <button class="odd-item" data-m="challenge"><b>Challenges</b><small>5 templates, many solutions</small></button>
        <button class="odd-item" data-m="daily"><b>Daily Experiment</b><small>Changes with local date</small></button>
        <button class="odd-item" data-m="discoveries"><b>Discoveries</b><small>${this.state.discoveries.length} logged</small></button>
        <button class="odd-item" data-m="import"><b>Import Challenge Code</b><small>Paste a shared scene JSON code</small></button>
        ${this.state.debug ? '<button class="odd-item" data-m="ad"><b>Simulate Ad</b><small>debug only</small></button>' : ''}
      </div>
      <div class="row" style="margin-top:12px;display:flex;gap:8px;">
        <button class="odd-btn secondary" data-m="close">Close</button>
      </div>
    `;
    this.panelEl.classList.add('open');
    card.querySelectorAll('[data-m]').forEach((el) => {
      el.addEventListener('click', () => {
        const k = (el as HTMLElement).dataset.m;
        if (k === 'close') this.closePanel();
        else if (k === 'quick' || k === 'free') {
          this.closePanel();
          this.cbs.onMode(k);
        } else if (k === 'challenge') {
          this.closePanel();
          this.openChallenges();
        } else if (k === 'daily') {
          this.closePanel();
          this.cbs.onDaily();
        } else if (k === 'discoveries') this.openDiscoveries();
        else if (k === 'import') this.openImport();
        else if (k === 'ad') {
          this.closePanel();
          this.cbs.onSimulateAd();
        }
      });
    });
  }

  openChallenges() {
    const card = this.panelEl.querySelector('[data-card]') as HTMLDivElement;
    card.innerHTML = `<h2>Challenges</h2><div class="odd-list"></div><div style="margin-top:12px"><button class="odd-btn secondary" data-close>Close</button></div>`;
    const list = card.querySelector('.odd-list')!;
    CHALLENGES.forEach((c) => {
      const prog = this.state.challengeProgress[c.id];
      const b = document.createElement('button');
      b.className = 'odd-item';
      b.innerHTML = `<b>${c.title}</b><small>${c.goal}</small><div class="meta">${prog?.completed ? '✓ cleared' : 'open'}${prog ? ` · best ${Math.round(prog.bestScore)}` : ''}</div>`;
      b.addEventListener('click', () => {
        this.closePanel();
        this.cbs.onChallenge(c);
      });
      list.appendChild(b);
    });
    card.querySelector('[data-close]')!.addEventListener('click', () => this.closePanel());
    this.panelEl.classList.add('open');
  }

  openDiscoveries() {
    const card = this.panelEl.querySelector('[data-card]') as HTMLDivElement;
    const items = this.state.discoveries
      .map(
        (d) =>
          `<div class="odd-item"><b>${d.title}</b><small>${d.detail}</small><div class="meta">${new Date(d.ts).toLocaleString()}</div></div>`,
      )
      .join('');
    card.innerHTML = `<h2>Discoveries</h2><div class="odd-list">${items || '<p style="opacity:.7">Run experiments to log discoveries.</p>'}</div><div style="margin-top:12px"><button class="odd-btn secondary" data-close>Close</button></div>`;
    card.querySelector('[data-close]')!.addEventListener('click', () => this.closePanel());
    this.panelEl.classList.add('open');
  }

  openImport() {
    const card = this.panelEl.querySelector('[data-card]') as HTMLDivElement;
    card.innerHTML = `
      <h2>Import Code</h2>
      <p style="font-size:12px;opacity:.8;margin-bottom:8px;">Paste a challenge / scene code</p>
      <textarea data-code style="width:100%;min-height:90px;border-radius:10px;border:1px solid #334;background:#0f1522;color:#e8eef7;padding:8px;"></textarea>
      <div style="display:flex;gap:8px;margin-top:12px;">
        <button class="odd-btn" data-go>Load</button>
        <button class="odd-btn secondary" data-close>Close</button>
      </div>`;
    card.querySelector('[data-close]')!.addEventListener('click', () => this.closePanel());
    card.querySelector('[data-go]')!.addEventListener('click', () => {
      const code = (card.querySelector('[data-code]') as HTMLTextAreaElement).value.trim();
      this.closePanel();
      this.cbs.onImportCode(code);
    });
    this.panelEl.classList.add('open');
  }

  closePanel() {
    this.panelEl.classList.remove('open');
  }

  setDebugText(text: string) {
    this.debugEl.textContent = text;
  }

  async runSimAd(onOffer: () => void, onStart: () => void, onComplete: () => void) {
    onOffer();
    this.adEl.classList.add('open');
    const bar = this.adEl.querySelector('[data-ad-bar]') as HTMLDivElement;
    const skip = this.adEl.querySelector('[data-ad-skip]') as HTMLButtonElement;
    skip.disabled = true;
    onStart();
    const total = 3000;
    const t0 = performance.now();
    await new Promise<void>((resolve) => {
      const tick = () => {
        const p = Math.min(1, (performance.now() - t0) / total);
        bar.style.width = `${p * 100}%`;
        const left = Math.ceil((1 - p) * 3);
        skip.textContent = p >= 1 ? 'Continue' : `Skip in ${left}…`;
        if (p >= 1) {
          skip.disabled = false;
          skip.onclick = () => {
            this.adEl.classList.remove('open');
            onComplete();
            resolve();
          };
          return;
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  }

  destroy() {
    this.root.remove();
  }
}
